import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, Button, DatePicker, Form, Input, Tag } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import type { z } from 'zod'
import type { billSchema } from '@super-mega/server/contracts/bill'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { BillConfirmationTable } from './bill-confirmation-table.tsx'
import { BillIssueNotice } from './bill-issue-notice.tsx'
import { BILL_STATUS, BILL_STATUS_COLORS } from './const/bill-search.ts'
import { useIssuedBills } from './hooks/use-issued-bills.ts'
import { OrganizationAccountSelect } from './organization-account-select.tsx'
import { isBillWithoutReceivableBalance } from './utils/bill-group.ts'

type Bill = z.infer<typeof billSchema>

export function GroupEditor({
  bills: initialBills,
  open = true,
  afterClose,
  onClose,
  onSaved
}: {
  bills: Bill[]
  open?: boolean
  afterClose?: () => void
  onClose: () => void
  onSaved: (id: number) => Promise<void>
}) {
  const [includedIds, setIncludedIds] = useState(() => initialBills.map((bill) => bill.id))
  const [current, setCurrent] = useState(1)
  const [initialValues] = useState(() => ({ billingMonth: dayjs() }))
  const selection = useIssuedBills(initialBills, 'group', includedIds)
  const { bills, ready } = selection
  const first = bills.at(0),
    trpc = useTRPC(),
    { message } = App.useApp(),
    [key] = useState(() => crypto.randomUUID())
  const save = useMutation(trpc.bill.createGroup.mutationOptions())
  const busy = save.isPending || selection.issuing
  const settledBills = bills.filter(isBillWithoutReceivableBalance)
  const removeBills = (ids: number[]) => {
    if (busy) return
    const removed = new Set(ids)
    setIncludedIds((previous) => previous.filter((id) => !removed.has(id)))
    setCurrent(1)
  }
  return (
    <ModalForm<{ billingMonth?: Dayjs; collectionAccountId?: number; note?: string }>
      open={open}
      title="创建收款账单组"
      width="min(76rem, calc(100vw - 2rem))"
      initialValues={initialValues}
      layout="vertical"
      modalProps={{ afterClose, onCancel: onClose, destroyOnHidden: true, mask: { closable: false } }}
      submitter={{ submitButtonProps: { loading: save.isPending, disabled: !ready || selection.issuing } }}
      onFinish={async (v) => {
        if (!ready || !first || busy) {
          return false
        }
        try {
          await onSaved(
            (
              await save.mutateAsync({
                billingMonth: v.billingMonth?.format('YYYY-MM') ?? null,
                organizationId: first.organizationId,
                customerId: first.customerId!,
                collectionAccountId: v.collectionAccountId ?? null,
                note: v.note ?? null,
                billIds: bills.map((b) => b.id),
                idempotencyKey: key
              })
            ).id
          )
          return true
        } catch (e) {
          void message.error(e instanceof Error ? e.message : '组单失败')
          return false
        }
      }}
    >
      <div className="flex flex-col gap-4">
        <BillIssueNotice {...selection} />
        {first && (
          <Alert
            type="info"
            title={`${first.organizationName} · ${first.customerName}`}
            description="保存草稿即占用账单；正式出单时冻结待收金额和账户资料。"
          />
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>本次纳入 {bills.length} 张账单；移除仅影响本次组单。</span>
          <Button
            disabled={busy || !settledBills.length}
            onClick={() => removeBills(settledBills.map((bill) => bill.id))}
          >
            一键移除已结清账单（{settledBills.length}）
          </Button>
        </div>
        <BillConfirmationTable
          bills={bills}
          current={current}
          onPageChange={setCurrent}
          disabled={busy}
          amountColumn={{
            title: '当前待收（元）',
            dataIndex: 'outstandingAmount',
            width: '10rem',
            align: 'right',
            render: formatAmount
          }}
          extraColumns={[
            {
              title: '状态',
              key: 'status',
              width: '6rem',
              render: (_, bill) => <Tag color={BILL_STATUS_COLORS[bill.status]}>{BILL_STATUS[bill.status].text}</Tag>
            },
            {
              align: 'center',
              title: '操作',
              key: 'actions',
              width: '5rem',
              fixed: 'right',
              render: (_, bill) => (
                <Button
                  type="link"
                  size="small"
                  disabled={busy}
                  aria-label={`移除账单 ${bill.billNo}`}
                  onClick={() => removeBills([bill.id])}
                >
                  移除
                </Button>
              )
            }
          ]}
        />
        <div>
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-[12rem_minmax(0,1fr)]">
            <Form.Item name="billingMonth" label="归属月份">
              <DatePicker picker="month" className="w-full" />
            </Form.Item>
            {first && (
              <Form.Item
                name="collectionAccountId"
                className="min-w-0"
                label="主体收款账户"
                tooltip="显示在收款单上，供客户付款使用；实际到账时仍需登记收款。"
              >
                <OrganizationAccountSelect purpose="bill.group.read" organizationId={first.organizationId} />
              </Form.Item>
            )}
          </div>
          <Form.Item name="note" label="出单备注">
            <Input.TextArea maxLength={5000} />
          </Form.Item>
        </div>
      </div>
    </ModalForm>
  )
}
